import type {
  ContentItem,
  StandardHeaderTextType,
  TableType,
} from '../../../models/ContentManagementInterface';
import { htmlToTextContent } from '../../../services/htmlToTextContent';
import { newHeadingContent } from '../../../services/TemplateUtils';
import { normalizeListIndentation } from '../../../services/normalizeListIndentation';
import type { ClipboardPayload } from './clipboardPayload';
import { extractLeadingHeadingFromHtml } from './extractLeadingHeading';
import { createContentBlock } from './newContentManagementEditorUtils';
import { EMPTY_PASTE_REASON } from './parsePastedContent';
import { isDelimited, plainTextToStandardHeaderContent } from './pastedContentHeuristics';
import { delimitedToRows } from './tableImport';
import { parseClipboardTable } from './tableModel';

export type CanvasPasteResult =
  | { kind: 'blocks'; blocks: ContentItem[] }
  | { kind: 'image'; file: File }
  | { kind: 'empty'; reason: string };

const TABLE_HTML = /<table[\s>]/i;
const RICH_HTML = /<(p|ul|ol|li|h[1-6]|br|strong|b|em|i)[\s>]/i;

function tableBlock(modelId: string, modelType: string, rows: TableType['properties']['rows']) {
  const block = createContentBlock('Table', modelId, modelType) as TableType;
  return { ...block, properties: { ...block.properties, rows } };
}

function textBlock(
  modelId: string,
  modelType: string,
  content: StandardHeaderTextType['properties']['content'],
  heading?: string,
) {
  const block = createContentBlock(
    'StandardHeaderText',
    modelId,
    modelType,
  ) as StandardHeaderTextType;
  return {
    ...block,
    properties: {
      ...block.properties,
      content,
      heading: heading ? newHeadingContent(heading) : block.properties.heading,
    },
  };
}

/**
 * Decides the best-fit block for a clipboard payload with no existing block to anchor the
 * decision (unlike `parsePastedContent`, which shapes a paste to fit whatever block is already
 * there). Structural content (an HTML/delimited table) becomes a real Table block; rich HTML or
 * plain prose becomes a Text Section; an image is handed back for the caller to run through the
 * same unscoped AI extraction the Documents tab uses (which can even split into several blocks).
 */
export function decideCanvasPaste(
  payload: ClipboardPayload,
  modelId: string,
  modelType: string,
): CanvasPasteResult {
  const html = (payload.html ?? '').trim();
  const text = payload.text ?? '';
  const image = payload.image ?? null;

  if (TABLE_HTML.test(html)) {
    const rows = parseClipboardTable(html, text);
    if (rows && rows.length > 0)
      return { kind: 'blocks', blocks: [tableBlock(modelId, modelType, rows)] };
  }
  if (isDelimited(text)) {
    const rows = delimitedToRows(text);
    if (rows.length > 0) return { kind: 'blocks', blocks: [tableBlock(modelId, modelType, rows)] };
  }
  if (RICH_HTML.test(html)) {
    const { heading, restHtml } = extractLeadingHeadingFromHtml(html);
    const content = htmlToTextContent(normalizeListIndentation(restHtml), true);
    return { kind: 'blocks', blocks: [textBlock(modelId, modelType, content, heading)] };
  }
  if (text.trim() !== '') {
    const { content, heading } = plainTextToStandardHeaderContent(text, true);
    return { kind: 'blocks', blocks: [textBlock(modelId, modelType, content, heading)] };
  }
  if (image) return { kind: 'image', file: image };
  return { kind: 'empty', reason: EMPTY_PASTE_REASON };
}
